import React, { createContext, useContext, useState, useEffect } from 'react';
import { getFunnel } from '../api/funnel';
import { setTrackingData } from '../helpers/trackingHelper';
import useDefaultFunnelRedirect from '../hooks/useDefaultFunnelRedirect';

const FunnelContext = createContext();
export const useFunnel = () => useContext(FunnelContext);

export const FunnelProvider = ({ children, funnelId }) => {
	const [funnelData, setFunnelData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const { redirectToDefaultFunnel, defaultFunnelNr } = useDefaultFunnelRedirect();

	useEffect(() => {
		const fetchFunnel = async () => {
			try {
				const storedFunnelData = localStorage.getItem('funnelData');
				if (storedFunnelData) {
					const storedFunnelDataId = JSON.parse(storedFunnelData)?.data?.id;
					if (Number(storedFunnelDataId) === Number(funnelId)) {
						const parsedData = JSON.parse(storedFunnelData);
						// Check if stored funnel is inactive
						if (parsedData?.data?.isActive === false) {
							// Only redirect if we're not already on the default funnel
							if (funnelId !== defaultFunnelNr) {
								redirectToDefaultFunnel();
								return;
							} else {
								// Already on default funnel, show error instead
								setError('The default funnel is currently inactive. Please contact support.');
								setLoading(false);
								return;
							}
						}
						setFunnelData(parsedData);
						setLoading(false);
						return;
					}
				}
				// If no stored data, fetch from API
				const data = await getFunnel(funnelId);
				
				// Check if funnel is inactive
				if (data?.data?.isActive === false) {
					// Only redirect if we're not already on the default funnel
					if (funnelId !== defaultFunnelNr) {
						redirectToDefaultFunnel();
						return;
					} else {
						// Already on default funnel, show error instead
						setError('The default funnel is currently inactive. Please contact support.');
						setLoading(false);
						return;
					}
				}
				
				setFunnelData(data);
				// Store the fetched data in localStorage
				localStorage.setItem('funnelData', JSON.stringify(data));
			} catch (err) {
				if (funnelId !== defaultFunnelNr) {
					redirectToDefaultFunnel();
					return;
				} else {
					// Already on default funnel, show error instead
					setError('The default funnel was not found. Please contact support.');
					setLoading(false);
					return;
				}
			} finally {
				setLoading(false);
			}
		};

		const setUtmParams = () => {
			const urlSearchParams = new URLSearchParams(window.location.search);

			const utmParams = {
				utm_source: urlSearchParams.get('utm_source') || 'direct',
				utm_medium: urlSearchParams.get('utm_medium') || 'none',
				utm_campaign: urlSearchParams.get('utm_campaign') || null,
				utm_content: urlSearchParams.get('utm_content') || null,
				traffic_type: urlSearchParams.get('utm_source') ? 'paid' : 'direct',
				fbc: urlSearchParams.get('fbclid') || urlSearchParams.get('fbc_id') || null,
			};

			setTrackingData(utmParams);
		};

		fetchFunnel();
		
		const currentTracking = localStorage.getItem('trackingParams');
		if (!currentTracking) {
			setUtmParams();
		} else {
			try {
				const parsed = JSON.parse(currentTracking);
				const isDefault = parsed.utm_source === 'direct' && parsed.utm_medium === 'none';
				
				if (isDefault) {
					setUtmParams();
				}
			} catch (e) {
				setUtmParams();
			}
		}
	}, [funnelId, redirectToDefaultFunnel, defaultFunnelNr]);

	const value = {
		funnelData,
		loading,
		setFunnelData,
		error,
	};

	return <FunnelContext.Provider value={value}>{children}</FunnelContext.Provider>;
};
